import * as React from "react";
import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import MarketPresenceMSATables from "./MarketPresenceMSATable";
import { KeyValue, Query } from "../../../../interfaces";
import { selectedStatesAlpha } from "../../../../refdata/FilterOptions";
import MarketShareStateList from "../../../../containers/MarketShareStateList";

interface Props {
  appState: Query;
  searchPills: React.ReactElement;
  updateUi: (key: string, value: string | KeyValue[]) => void;
}
const useStyles = makeStyles(theme => ({
  body: {
    color: theme.palette.primary.main,
    fontWeight: 550,
    fontSize: 16,
    lineHeight: "24px",
    fontStyle: "normal",
  },
  footer: {
    color: theme.palette.primary.main,
    lineHeight: "24px",
    fontSize: "16px",
    fontWeight: 400,
    textAlign: "left",
  },
  heading: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
  heading2: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 18,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
  moreInfo: {
    "color": "#0000ff",
    "fontWeight": "bold",
    "fontSize": 16,
    "lineHeight": "24px",
    "paddingBottom": "10px",
    "fontStyle": "normal",
    "&:hover, &.Mui-focusVisible": {
      textDecoration: "underline",
      cursor: "pointer",
    },
  },
  openCloseAll: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },
  footerText: {
    color: theme.palette.primary.main,
    width: 900,
    minWidth: 900,
    fontSize: 16,
  },
}));

export const appName = (stalpbr: string, displayType: string) => {
  const options: KeyValue = {
    "statesByCounty":
      stalpbr && stalpbr !== "ALL"
        ? `${
            selectedStatesAlpha.find(
              (state: KeyValue) => state.value === stalpbr,
            )?.label
          } by Counties`
        : "Nationally By State & County",
    "msas": "Metropolitan Statistical Areas (MSAs)",
    "": "",
  };
  return options[displayType];
};

export default function MarketPresenceTables({
  appState,
  searchPills,
  updateUi,
}: Props) {
  const classes = useStyles();
  const [initialLoad, setInitialLoad] = React.useState(true);
  React.useEffect(() => {
    if (initialLoad) {
      window.scrollTo(0, 0);
      setInitialLoad(false);
      !appState.ui.displayResults &&
        updateUi("displayResults", "statesByCounty");
    }
  }, [appState.ui.displayResults, initialLoad, updateUi]);
  const headingTitle = (
    <>
      <Typography className={classes.heading}>
        Market Presence Results -{" "}
        {appName(appState.filters.stalpbr, appState.ui.displayResults)}
      </Typography>
      <Typography className={classes.heading2}>
        {!appState.ui.institutionName && appState.filters.cert
          ? "No Results"
          : `${
              appState.filters.bhcRSSD
                ? "Combined Insured Subsidiaries of "
                : ""
            }${appState.ui.institutionName}`}
      </Typography>
    </>
  );

  const footer1 = (
    <Typography className={classes.footer}>
      *The total deposits for the bank holding company is the combined total
      domestic deposits of the underlying insured subsidiaries.
    </Typography>
  );

  const footer2 = (
    <Typography className={classes.footer}>
      <b>Note about this data for BHCs or Bank Holding Companies:</b>
      <br />
      This search will return results for top regulatory bank holding companies
      and their FDIC-insured subsidiaries. The regulatory top holder is assigned
      by the Federal Reserve Board based on ownership and control percentages.
      Thrift bank holding company information is not available. Official bank
      holding company information can be found by visiting the website of the
      Federal Reserve System - National Information Center.
    </Typography>
  );

  return (
    <>
      {headingTitle}
      {appState.ui.displayResults === "statesByCounty" && (
        <MarketShareStateList searchPills={searchPills} />
      )}
      {appState.ui.displayResults === "msas" && (
        <MarketPresenceMSATables
          appState={appState}
          updateUi={updateUi}
          searchPills={searchPills}
        />
      )}
      {appState.filters.bhcRSSD && footer1}
      {appState.filters.bhcRSSD && <br />}
      {appState.filters.bhcRSSD && footer2}
    </>
  );
}
